import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { assert } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { BrowserControlService } from '../../../cross-team-shared/misc-services/browser-control.service';
import { MessageModalComponent } from '../../../cross-team-shared/misc-services/message-modal/message-modal.component';
import { TokenManagementService } from '../../../cross-team-shared/misc-services/token-management.service';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { WorkspaceConfigService } from '../../../shared/misc-services/workspace-config.service';
import { WorkspaceLoadingService } from '../../../shared/misc-services/workspace-loading.service';
import { StepResource } from '../../../shared/resources/step-resource.service';
import { ExecutionQueueService } from '../../../shared/save-manager/execution-queue.service';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { DraftSubmissionService } from '../submissions/draft-submission.service';
import { PersistenceManager } from '../submissions/persistence-manager';
import { TimerService } from '../timer-badge/timer.service';

export type StopWorkSource = 'submit' | 'pause' | 'production_workspace';

@Injectable()
export class StopWorkService extends InjectableSuperclass {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private workspaceConfigService = inject(WorkspaceConfigService);
  private browserControl = inject(BrowserControlService);
  private stepResource = inject(StepResource);
  private dialogService = inject(MatDialog);
  private eventTrackingService = inject(EventTrackingService);
  private workspaceLoadingService = inject(WorkspaceLoadingService);
  private persistenceManager = inject(PersistenceManager);
  private timerService = inject(TimerService);
  private executionQueue = inject(ExecutionQueueService);
  private draftSubmissionService = inject(DraftSubmissionService);
  private tokenService = inject(TokenManagementService);

  stopWork(source: StopWorkSource, resumeOnError = true) {
    this.timerService.pause();
    this.workspaceLoadingService.isLoading$.next(true);
    this.trackStopWorkEvent(source);

    if (!this.taskWorkspaceStore.isUsingObjects()) {
      void this.stopWorkObjectless(resumeOnError);
      return;
    }

    this.subscribeTo(this.persistenceManager.stopWork(source), (response) =>
      this.browserControl.navigateTo(response.redirect_to),
    );
  }

  private stopWorkObjectless(resumeOnError: boolean) {
    return this.executionQueue.enqueue(async () => {
      try {
        const token = await this.tokenService.getToken('access_token');

        await this.draftSubmissionService.saveDraft(true, token);

        const { projectId, stepId } = this.workspaceConfigService;
        assert(stepId, 'stepId is defined when the project is loaded.');

        const resp = await lastValueFrom(
          this.stepResource.stopWork$(projectId, stepId, token),
        );

        this.browserControl.navigateTo(resp.redirect_to);
      } catch {
        await firstValueFrom(this.resumeWorkAfterError$(resumeOnError));
      }
    });
  }

  private trackStopWorkEvent(source: string) {
    const task = this.taskWorkspaceStore.state().task;
    this.eventTrackingService.track({
      id: 'stop_work',
      bucket: 'untracked',
      workspace: 'production',
      step_id: this.workspaceConfigService.stepId,
      task_id: task.id,
      round: task.round,
      source,
    });
  }

  private warnWorkNotSaved$() {
    const modal = this.dialogService.open(MessageModalComponent, {
      data: {
        title: 'Error',
        text: 'Your work was not saved due to an error. Try stopping work again.',
      },
      disableClose: true,
    });
    return modal.afterClosed();
  }

  private resumeWorkAfterError$(resumeOnError: boolean) {
    this.workspaceLoadingService.isLoading$.next(false);
    resumeOnError && this.timerService.resume();
    return this.warnWorkNotSaved$();
  }
}
